import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
} from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { getOrgDashboard, getOrgPolicy, updateOrgPolicy } from '../lib/api';
import { fmtDate } from '../lib/format';
import { useCallback, useState } from 'react';

/**
 * /o/:orgSlug/settings — Basic organization settings.
 *
 * Shows the org's display name, plan badge, a list of child
 * workspaces, and a policy governance toggle (V91-5-5).
 */

export function OrgSettings() {
  const { orgSlug } = useParams<{ orgSlug: string }>();

  const dashboardQuery = useQuery({
    queryKey: queryKeys.orgSettings(orgSlug ?? ''),
    queryFn: () => getOrgDashboard(orgSlug!),
    enabled: !!orgSlug,
  });
  const dashboard = {
    data: dashboardQuery.data ?? null,
    error: dashboardQuery.error?.message ?? null,
    loading: dashboardQuery.isLoading,
  };
  const policyQuery = useQuery({
    queryKey: queryKeys.orgPolicy(orgSlug ?? ''),
    queryFn: () => getOrgPolicy(orgSlug!),
    enabled: !!orgSlug,
  });
  const policy = {
    data: policyQuery.data ?? null,
    error: policyQuery.error?.message ?? null,
    loading: policyQuery.isLoading,
    refetch: policyQuery.refetch,
  };

  const [saving, setSaving] = useState(false);

  const handlePolicyChange = useCallback(
    async (value: string) => {
      if (!orgSlug || saving) return;
      setSaving(true);
      try {
        await updateOrgPolicy(orgSlug, value);
        policy.refetch();
      } catch {
        // Server returns 404 for non-admins; refetch to stay in sync.
        policy.refetch();
      } finally {
        setSaving(false);
      }
    },
    [orgSlug, saving, policy],
  );

  if (dashboard.loading) return <Spinner label="Loading organization settings" />;
  if (dashboard.error) return <Alert variant="error">{dashboard.error}</Alert>;
  if (!dashboard.data) return null;

  const org = dashboard.data.organization;
  const workspaces = dashboard.data.workspaces;

  const policySource = policy.data?.policy_source ?? 'frameworks_only';
  const policySetAt = policy.data?.policy_source_set_at ?? null;
  const policySetBy = policy.data?.policy_source_set_by ?? null;
  const isAdmin = policy.data?.caller_role === 'organization_admin';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Organization settings"
        subtitle={org.display_name}
      />

      {/* Org info card */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
              Display name
            </div>
            <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
              {org.display_name}
            </div>
          </div>
          <Badge tone="accent">{org.plan}</Badge>
        </div>
        <div>
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
            Organization slug
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-2xs)', color: 'var(--text-dim)' }}>
            {org.slug}
          </div>
        </div>
      </div>

      {/* Child workspaces */}
      <section style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
          Workspaces ({workspaces.length})
        </div>
        {workspaces.length === 0 ? (
          <EmptyState
            title="No workspaces"
            description="This organization has no workspaces yet."
          />
        ) : (
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
            {workspaces.map((ws) => (
              <li key={ws.workspace_id}>
                <Link
                  to={`/w/${ws.slug}/app`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--space-3) var(--space-4)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-card)',
                    background: 'var(--surface)',
                    textDecoration: 'none',
                    gap: 'var(--space-3)',
                  }}
                >
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{ws.name}</span>
                  <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {ws.score}% · {ws.item_count} items
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Policy governance (V91-5-5) */}
      {!policy.loading && !policy.error && policy.data && (
        <Card>
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
              Policy governance
            </h3>

            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', cursor: isAdmin ? 'pointer' : 'default' }}>
                <input
                  type="radio"
                  name="policy_source"
                  value="frameworks_only"
                  checked={policySource === 'frameworks_only'}
                  disabled={!isAdmin || saving}
                  onChange={() => handlePolicyChange('frameworks_only')}
                />
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>Framework standards only</div>
                  <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
                    NIST AI RMF, FedRAMP, ISO 42001, SOC 2+AI.
                    Jurisdiction-specific requirements will not appear.
                  </div>
                </div>
              </label>

              <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', cursor: isAdmin ? 'pointer' : 'default' }}>
                <input
                  type="radio"
                  name="policy_source"
                  value="frameworks_and_legislation"
                  checked={policySource === 'frameworks_and_legislation'}
                  disabled={!isAdmin || saving}
                  onChange={() => handlePolicyChange('frameworks_and_legislation')}
                />
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>Framework + Jurisdiction</div>
                  <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
                    Framework standards plus applicable jurisdiction requirements
                    based on deployment geography.
                  </div>
                </div>
              </label>
            </div>

            {policySetAt && (
              <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                Last changed: {fmtDate(policySetAt)}{policySetBy ? ` by ${policySetBy}` : ''}
              </div>
            )}

            {!isAdmin && (
              <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-dim)' }}>
                Contact your organization admin to change this setting.
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
